import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppSettings, AppState, CurrentDate, CurrentLocation } from './model';
import { APP_ACTIONS, APP_LOADERS } from './utils';
import { User } from '@supabase/supabase-js';
import { CleanerProfile } from '../cleaners/model';
import { persistReducer } from 'redux-persist';

const initialState: AppState = {
  appLoaders: {},
  searchForm: {},
  settings: {
    mode: 'user',
    enableNotifications: true,
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setCurrentUser: (state, { payload }: PayloadAction<AppState['user']>) => {
      state.user = payload;
    },
    setLoginData: (
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: keyof AppState['loginData']; value: string }>,
    ) => {
      state.loginData = { ...(state.loginData ?? {}), [key]: value };
    },
    updateSearchForm: (
      state,
      { payload }: PayloadAction<AppState['searchForm']>,
    ) => {
      state.searchForm = payload;
    },
    resetSearchForm: (state) => {
      state.searchForm = initialState.searchForm;
    },
    resetLoginData: (state) => {
      state.loginData = initialState.loginData;
    },
    resetCurrentUser: (state) => {
      state.user = initialState.user;
    },
    setCleanerProfile: (
      state,
      { payload }: PayloadAction<CleanerProfile | undefined>,
    ) => {
      state.cleanerProfile = payload;
    },
    toggleLoader: (
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: APP_LOADERS; value: boolean }>,
    ) => {
      state.appLoaders[key] = value;
    },
    changeSettings: <K extends keyof AppSettings>(
      state: AppState,
      {
        payload: { key, value },
      }: PayloadAction<{ key: K; value: AppSettings[K] }>,
    ) => {
      state.settings[key] = value;
    },

    reset: () => initialState,
  },
});

const extraActions = {
  initUser: createAction<User>(APP_ACTIONS.INIT_USER),
};

export const {
  reset,
  setCleanerProfile,
  setCurrentUser,
  setLoginData,
  resetLoginData,
  resetCurrentUser,
  updateSearchForm,
  initUser,
  toggleLoader,
  changeSettings,
  resetSearchForm,
} = { ...appSlice.actions, ...extraActions };

import storage from 'redux-persist/lib/storage';

const persistConfig = {
  key: 'root',
  storage,
};

export const appReducer = persistReducer(persistConfig, appSlice.reducer);
